// oxlint-disable-next-line no-restricted-imports
import { useAuth0 } from '@auth0/auth0-react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { UserType } from '@luminovo/api-contracts';
import { useAuth } from '@luminovo/auth';
import { compareByString, id, isProductionEnvironment } from '@luminovo/commons';
import { analytics, useHttpQuery } from '@luminovo/data-stack';
import {
    AvatarIcon,
    colorSystem,
    Divider,
    linkOptions,
    MenuItem,
    Text,
    Tooltip,
    useCanOpen,
} from '@luminovo/design-system';
import { ListItem, Menu, type MenuProps } from '@mui/material';
import {
    ArrowLeftRightIcon,
    ExternalLinkIcon,
    FileTextIcon,
    InfoIcon,
    LogOutIcon,
    SettingsIcon,
    ShieldCheckIcon,
    ToggleRightIcon,
} from 'lucide-react';
import React, { type JSX } from 'react';

import { useCurrentUserDetailsContext, useUserType } from '@/shared/data/contexts/CurrentUserDetailsContext';
import { useTenantDetailsContext } from '@/shared/data/contexts/TenantDetailsContext';
import { Protected } from '@/shared/permissions/Protected';
import { LanguageSelection } from '@/shared/ui/LanguageSelection';
import { showCookieConsentSettings } from '@/shared/utils/services';

import { NavbarTertiaryIconButton } from './NavbarTertiaryIconButton';
import { NewNavigationSwitch } from './NewNavigationSwitch';

const getHelpCenterLink = (): string => {
    return 'https://help.luminovo.com';
};

const settingsLink = linkOptions({ to: '/settings/organization' });
const companyLink = linkOptions({ to: '/settings/company' });

const TenantSelection: React.FunctionComponent = () => {
    const { all_tenants_for_user } = useCurrentUserDetailsContext();
    const userType = useUserType();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    if (all_tenants_for_user.length < 2 || userType !== UserType.Internal) {
        return null;
    }

    return (
        <>
            <MenuItem
                label={t`Switch tenant`}
                startIcon={<ArrowLeftRightIcon />}
                onClick={(event: React.MouseEvent<HTMLLIElement>) => setAnchorEl(event.currentTarget)}
            />
            <Menu
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                slotProps={{
                    paper: {
                        style: {
                            transform: 'translateX(-100%)',
                        },
                    },
                }}
            >
                <ListItem>
                    <Text variant={'body-small'} color={colorSystem.neutral[7]}>
                        <Trans>Tenants</Trans>
                    </Text>
                </ListItem>
                {Array.from(all_tenants_for_user)
                    .sort(compareByString)
                    .map((key) => (
                        <MenuItem
                            key={key}
                            label={
                                <Tooltip variant={'white'} title={`${key}.luminovo.com`} placement="left">
                                    <span>{key}</span>
                                </Tooltip>
                            }
                            onClick={() => {
                                setAnchorEl(null);
                                window.open(`https://${key}.luminovo.com`, '_blank', 'noopener');
                            }}
                        />
                    ))}
            </Menu>
        </>
    );
};

export const Avatar: React.FunctionComponent<{ variant: 'light' | 'dark'; style?: React.CSSProperties }> = React.memo(
    ({ variant, style }) => {
        const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
        const { user: currentUser } = useCurrentUserDetailsContext();

        return (
            <>
                <NavbarTertiaryIconButton
                    aria-label={t`Account`}
                    id={id('navbar/button_avatar')}
                    style={{ margin: '12px', marginRight: '0', height: '20px', width: '20px', ...style }}
                    variant={variant}
                    disableTouchRipple
                    edge="end"
                    aria-haspopup="true"
                    onClick={(event) => setAnchorEl(event.currentTarget)}
                >
                    <AvatarIcon
                        size="small"
                        user={{
                            firstName: currentUser.first_name,
                            lastName: currentUser.last_name,
                            email: currentUser.email,
                        }}
                    />
                </NavbarTertiaryIconButton>
                <AccountMenu anchorEl={anchorEl} onClose={() => setAnchorEl(null)} />
            </>
        );
    },
);

/** The account menu, below the navbar's avatar or beside another trigger, such as the new navigation's user. */
export function AccountMenu({
    anchorEl,
    onClose,
    anchorOrigin = { vertical: 'bottom', horizontal: 'center' },
    transformOrigin = { vertical: 'top', horizontal: 'center' },
}: { anchorEl: HTMLElement | null; onClose: () => void } & Pick<
    MenuProps,
    'anchorOrigin' | 'transformOrigin'
>): JSX.Element {
    const { tenantDetails } = useTenantDetailsContext();
    const { logout } = useAuth({
        auth0OrgId: tenantDetails.external_auth_org_id ?? undefined,
    });
    const { user: currentUser, userType } = useCurrentUserDetailsContext();
    const { user } = useAuth0();
    const canOpen = useCanOpen();

    // Not a suspense query: the navbar is on every page and renders before these footer links have loaded.
    const { data: customerPortalSettings } = useHttpQuery(
        'GET /customer-portal-settings',
        {},
        {
            staleTime: 60_000,
            select: (res) => {
                const { imprint_link, statement_link, terms_and_conditions_link } = res;
                return { imprint_link, statement_link, terms_and_conditions_link };
            },
        },
    );

    return (
        <Menu
            anchorEl={anchorEl}
            anchorOrigin={anchorOrigin}
            transformOrigin={transformOrigin}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={onClose}
        >
            <MenuItem disabled label={currentUser.email} />
            {/* Organization settings are for Internal users; the route also lets customers in, who land on
                Account security. */}
            {userType === UserType.Internal && canOpen(settingsLink) && (
                <MenuItem label={t`Settings`} startIcon={<SettingsIcon />} {...settingsLink} onClick={onClose} />
            )}
            <MenuItem
                label={t`Privacy & Cookies`}
                startIcon={<ToggleRightIcon />}
                onClick={showCookieConsentSettings}
            />
            {canOpen(companyLink) && (
                <MenuItem label={t`Company settings`} startIcon={<SettingsIcon />} {...companyLink} onClick={onClose} />
            )}
            {customerPortalSettings && [
                customerPortalSettings.imprint_link && customerPortalSettings.imprint_link !== '' && (
                    <MenuItem
                        key="imprintLink"
                        label={t`Imprint`}
                        startIcon={<ExternalLinkIcon />}
                        onClick={() => window.open(customerPortalSettings.imprint_link ?? '', '_blank', 'noopener')}
                    />
                ),
                customerPortalSettings.statement_link && customerPortalSettings.statement_link !== '' && (
                    <MenuItem
                        key="privacyLink"
                        label={t`Privacy statement`}
                        startIcon={<ShieldCheckIcon />}
                        onClick={() => window.open(customerPortalSettings.statement_link ?? '', '_blank', 'noopener')}
                    />
                ),
                customerPortalSettings.terms_and_conditions_link &&
                    customerPortalSettings.terms_and_conditions_link !== '' && (
                        <MenuItem
                            key="termsAndConditionsLink"
                            label={t`Terms & Conditions`}
                            startIcon={<FileTextIcon />}
                            onClick={() =>
                                window.open(
                                    customerPortalSettings.terms_and_conditions_link ?? '',
                                    '_blank',
                                    'noopener',
                                )
                            }
                        />
                    ),
            ]}
            <Protected requiredPermissions={() => userType === UserType.Internal}>
                <MenuItem
                    label={t`Help center`}
                    startIcon={<InfoIcon />}
                    onClick={() => window.open(getHelpCenterLink(), '_blank', 'noopener')}
                />
            </Protected>
            {!isProductionEnvironment() && [
                <Divider key="divider" />,
                <MenuItem key="tools" disabled label="Testing tools" />,
                <MenuItem
                    key={'auth0'}
                    label="Auth0"
                    onClick={() => {
                        const urlEncoded = encodeURIComponent(user?.sub ?? '');
                        const encodedId = window.btoa(urlEncoded);
                        const url = `https://manage.auth0.com/dashboard/eu/luminovo-dev/users/${encodedId}`;
                        window.open(url, '_blank', 'noopener');
                    }}
                />,
                <MenuItem
                    key={'version'}
                    label={
                        <Tooltip title={`SHA:${Boolean(window.EPIBATOR_SHA) ? window.EPIBATOR_SHA : `unknown`}`}>
                            <span>{`Version: ${
                                Boolean(window.LUMINOVO_VERSION) ? window.LUMINOVO_VERSION : 'unknown'
                            }`}</span>
                        </Tooltip>
                    }
                    onClick={() => {
                        // Added this confirmation because I had previously accidentally pressed this button
                        // and reloading the page takes a while...
                        if (window.confirm('Are you sure you want to reload the page?')) {
                            window.location.reload();
                        }
                    }}
                />,
            ]}
            <Divider />
            <LanguageSelection />
            <TenantSelection />
            <NewNavigationSwitch onClose={onClose} />
            <MenuItem
                label={t`Log out`}
                startIcon={<LogOutIcon />}
                onClick={() => {
                    analytics.track('logout');
                    logout();
                }}
            />
            <MenuItem
                disabled
                label={
                    <Text variant="caption" color={colorSystem.neutral[6]}>
                        Luminovo v{window.LUMINOVO_VERSION || '0'}
                    </Text>
                }
            />
        </Menu>
    );
}
